import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import LocationService from '../../services/locationService';
import SearchableSelect from '../../components/common/searchable-select';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';
import { getUserEmail } from '../../services/apiClient';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 8;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ─────────────────────────────────────────────────────────────
// Building Modal
// ─────────────────────────────────────────────────────────────

const BuildingModal = ({ mode, data, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th:     data?.name_th     || '',
        name_en:     data?.name_en     || '',
        id_province: data?.id_province ? String(data.id_province) : '',
        id_district: data?.id_district ? String(data.id_district) : '',
        map:         data?.map         || '',
    });

    const [provinces,        setProvinces]        = useState([]);
    const [districts,        setDistricts]        = useState([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const isEdit = mode === 'edit';

    // โหลด provinces ตอนเปิด modal
    useEffect(() => {
        LocationService.SearchProvince({ status: 1 }).then((res) => {
            setProvinces(res.data.result || []);
        }).catch(() => {});
    }, []);

    // ถ้า edit และมี id_province อยู่แล้ว → โหลด districts ทันที
    useEffect(() => {
        if (data?.id_province) {
            setLoadingDistricts(true);
            LocationService.SearchDistrict({ id_province: data.id_province, status: 1 })
                .then((res) => setDistricts(res.data.result || []))
                .catch(() => {})
                .finally(() => setLoadingDistricts(false));
        }
    }, []);

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleProvinceChange = async (id) => {
        setForm((prev) => ({ ...prev, id_province: id, id_district: '' }));
        setDistricts([]);
        if (!id) return;
        setLoadingDistricts(true);
        try {
            const res = await LocationService.SearchDistrict({ id_province: id, status: 1 });
            setDistricts(res.data.result || []);
        } catch (e) {}
        finally { setLoadingDistricts(false); }
    };

    const handleSave = () => {
        const provinceName = provinces.find(p => String(p.id) === String(form.id_province))?.name_th || '';
        const districtName = districts.find(d => String(d.id) === String(form.id_district))?.name_th || '';
        const location = [provinceName, districtName].filter(Boolean).join(', ');
        setTimeout(() => onSave({ ...form, location }), 0);
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Edit' : 'เพิ่มอาคารใหม่ New Building'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Name (TH)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_th}
                                    onChange={(e) => handleChange('name_th', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Name (EN)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_en}
                                    onChange={(e) => handleChange('name_en', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Province</label>
                                <SearchableSelect
                                    id="building-province"
                                    name="id_province"
                                    value={form.id_province}
                                    onChange={(e) => handleProvinceChange(e.target.value)}
                                    options={provinces.map((p) => ({ value: String(p.id), label: p.name_th }))}
                                    placeholder="Select"
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>District</label>
                                <SearchableSelect
                                    id="building-district"
                                    name="id_district"
                                    value={form.id_district}
                                    onChange={(e) => handleChange('id_district', e.target.value)}
                                    disabled={!form.id_province || loadingDistricts}
                                    options={districts.map((d) => ({ value: String(d.id), label: d.name_th }))}
                                    placeholder={loadingDistricts ? 'กำลังโหลด...' : 'Select'}
                                    noOptionsMessage={loadingDistricts ? 'กำลังโหลด...' : 'ไม่พบข้อมูล'}
                                />
                            </div>
                        </div>
                        <div className="mc-form-row">
                            <div className="mc-form-group" style={{ flex: 1 }}>
                                <label>Map URL</label>
                                <input
                                    type="text"
                                    placeholder="https://maps.google.com/..."
                                    value={form.map}
                                    onChange={(e) => handleChange('map', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

const AdminBuilding = () => {
    // ── Data state ──
    const [data, setData]     = useState([]);
    const [search, setSearch] = useState('');
    const [modal, setModal]   = useState(null);
    const [newId, setNewId]   = useState(null);

    // ── Pagination state ──
    const [currentPage, setCurrentPage] = useState(1);

    // ── Refs ──
    const highlightRef = useRef(null);

    useEffect(() => {
        loadData();
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadData = async () => {
        try {
            const resp = await LocationService.SearchBuilding({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            const sorted = [...resp.data.result].sort((a, b) =>
                (a.name_en || '').localeCompare(b.name_en || '', 'en')
            );
            setData(sorted);
        } catch (err) {
            console.error('loadData error:', err?.response?.data);
        }
    };

    // ─────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────

    const handleSave = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขอาคาร' : 'ยืนยันการเพิ่มอาคารใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th:          form.name_th,
                name_en:          form.name_en || form.name_th,
                location:         form.location || '-',
                map:              form.map || '',
                id_province:      Number(form.id_province) || 0,
                id_district:      Number(form.id_district) || 0,
                id_business_unit: currentData?.id_business_unit || 1,
                status:           1,
                create_by:        getUserEmail(),
                update_by:        getUserEmail(),
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await LocationService.UpdateBuilding(currentData?.id, payload);
            } else {
                const resp = await LocationService.CreateBuilding(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขอาคารสำเร็จแล้ว' : 'เพิ่มอาคารสำเร็จแล้ว',
            });

            await loadData();

            if (currentMode === 'add' && createdId) {
                setNewId(createdId);
                if (highlightRef.current) clearTimeout(highlightRef.current);
                highlightRef.current = setTimeout(() => setNewId(null), 10000);
            }
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDelete = async (row) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบ',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await LocationService.DeleteBuilding(row.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบอาคารออกแล้ว' });
            loadData();
        } catch (err) { hideLoading(); showError(); }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    // ─────────────────────────────────────────────────────
    // Derived data (filter + pagination)
    // ─────────────────────────────────────────────────────

    const filtered = data.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.name_th  || '').toLowerCase().includes(q) ||
            (row.name_en  || '').toLowerCase().includes(q) ||
            (row.location || '').toLowerCase().includes(q)
        );
    });

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    return (
        <>
            <div className="mc-page">
                {/* ── Header ── */}
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Building</h2>
                        <p className="mc-subtitle">รายชื่ออาคารปลายทาง</p>
                    </div>
                    <button className="mc-btn mc-btn--new" onClick={() => setModal({ mode: 'add' })}>
                        <i className="ri-add-line" /> New Building
                    </button>
                </div>

                <div className="mc-card">
                    {/* ── Toolbar ── */}
                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Select"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="mc-toolbar__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Name TH</th>
                                    <th>Name EN</th>
                                    <th>Province</th>
                                    <th>District</th>
                                    <th>Map</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((row, idx) => {
                                        const [province, district] = (row.location || '').split(', ');
                                        return (
                                        <tr key={row.id} className={row.id === newId ? 'mc-row-new' : ''}>
                                            <td className="mc-col-no">{(safePage - 1) * ROWS_PER_PAGE + idx + 1}</td>
                                            <td>{row.name_th}</td>
                                            <td className="mc-col-muted">{row.name_en || '-'}</td>
                                            <td className="mc-col-muted">{province || '-'}</td>
                                            <td className="mc-col-muted">{district || '-'}</td>
                                            <td>
                                                {row.map ? (
                                                    <a href={row.map} target="_blank" rel="noreferrer" className="mc-link">
                                                        ดูแผนที่
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            <td>
                                                <div className="mc-actions">
                                                    <button
                                                        className="mc-action-btn mc-action-btn--edit"
                                                        onClick={() => setModal({ mode: 'edit', data: row })}
                                                    >
                                                        <i className="ri-pencil-line" />
                                                    </button>
                                                    <button
                                                        className="mc-action-btn mc-action-btn--delete"
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        <i className="ri-delete-bin-line" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* ── Modal ── */}
            {modal && (
                <BuildingModal
                    mode={modal.mode}
                    data={modal.data}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
        </>
    );
};

export default AdminBuilding;